// Public entry point for the local-first BSB Bible data layer
// (M.3.6.3.b).
//
// Posture decisions for this chunk:
// - Gated behind explicit caller invocation. Not wired in `main.tsx`
//   this chunk; the first consumer is the M.3.7 picker. Calling this
//   from boot would cost ~5.43 MB of IDB writes on every install
//   before the user has any reason to need scripture.
// - Meta-first availability. Once a user has hydrated locally, an
//   offline manifest fetch never breaks the app. We log a warning
//   and treat the local IDB as authoritative; the next online run
//   will pick up any sha update.
// - Two hydration shapes since 2026-08-25, split by whether a complete
//   corpus already exists:
//   * FIRST-TIME (no meta, or meta present but the count witness says
//     the corpus is partial): RESUMABLE per-book hydration. Each book
//     is fetched, validated, and written individually; a checkpoint
//     inventory row records progress so an interrupted or
//     partially-failed run RESUMES with only the gaps instead of
//     restarting all 66 from zero. One failed book no longer discards
//     its 65 siblings, and peak memory holds ~CONCURRENCY books, not
//     the whole corpus. The meta row is written only after ALL books
//     are present and the count witness verifies — meta stays the sole
//     "complete Bible on this device" signal.
//   * UPDATE (meta present, sha mismatch): the original ATOMIC shape.
//     Fetch + validate all 66 into memory, then the single multi-store
//     transaction in `bsb-db.ts:hydrateAtomic` swaps the corpus in one
//     boundary — the OLD complete version stays fully readable until
//     the new one has completely arrived, and a failed update keeps
//     the old corpus authoritative. Per-book incremental writes would
//     regress that staged-update guarantee, so updates deliberately
//     keep the all-or-nothing shape.
// - Storage cost: ~5.43 MB into IDB only. IDB is the single
//   authoritative source for Bible data. The service worker passes
//   `/bible/bsb/*` straight through (offline-first Chunk 1,
//   2026-06-13), so the BSB JSON is NOT double-stored in Cache
//   Storage. (Pre-Chunk-1 the network-first SW cached every successful
//   GET, adding a redundant ~5.43 MB — that double-store is now gone;
//   this was the previously-banked SW cleanup.)
// - Module-level singleton (`inFlight`) is same-tab dedupe only.
//   Cross-tab concurrency is handled by IDB's per-store transaction
//   serialization, not by this dedupe. Two tabs racing this function
//   may each fetch the manifest + 66 books; the IDB writes serialize
//   so neither sees a half-written state.
// - Update-with-fallback. If a sha mismatch triggers a rehydrate and
//   any step fails (book fetch, validation, IDB write), the bootstrap
//   resolves successfully and the previous IDB cache stays
//   authoritative. The next call retries the update. Hard failure is
//   reserved for the meta-absent + manifest-failed case, where there
//   is no local data to fall back on.
// - Requested-book-first fast path (2026-08-25). `ensureBookAvailable`
//   serves ONE book: local IDB first (zero network wait when the book
//   is on device — no manifest round-trip), else a direct single-book
//   fetch validated against CANONICAL_BOOKS, rendered from memory and
//   persisted opportunistically (a storage failure never blocks
//   reading). This decouples "read this chapter now" from the full
//   66-book hydration, so one failed request on a fresh device can no
//   longer block every chapter. Full hydration remains the sole
//   authority for the complete-offline promise (the meta row) and for
//   sha updates; per-book saves are cache warms that hydrateAtomic
//   clears + rewrites wholesale.

import { getReadingAccessSnapshot } from './reading-access';
import {
  clearBsbInventory,
  countBsbBooks,
  getBsbBook,
  getBsbBookSlugs,
  getBsbInventory,
  getBsbMeta,
  hydrateAtomic,
  saveBsbBook,
  saveBsbMeta,
  setBsbInventory,
} from '@/lib/bsb-db';
import { BibleHydrationError } from '@/lib/bsb-errors';
import { CANONICAL_BOOKS } from '@/lib/bsb-types';
import type {
  BsbBook,
  BsbManifest,
  BsbMeta,
} from '@/lib/bsb-types';
import { useBibleBootstrapStore } from '@/lib/bsb-progress-store';
import { clearSearchCache } from '@/lib/bsb-search';
import { reportBibleEvent } from '@/lib/bible-telemetry';

const MANIFEST_URL = '/bible/bsb/manifest.json';
const bookUrl = (slug: string) => `/bible/bsb/${slug}.json`;
const MANIFEST_TIMEOUT_MS = 15000;
const BOOK_TIMEOUT_MS = 30000;
const CONCURRENCY = 4;
const SHA256_HEX = /^[0-9a-f]{64}$/;

// Defense-in-depth check that the manifest's slug set matches the
// client-side canonical list. The single source of truth is
// CANONICAL_BOOKS in `bsb-types.ts` (M.3.6.3.c consolidation); we
// derive a Set here for the O(1) lookup the manifest validator needs.
const CANONICAL_SLUGS: ReadonlySet<string> = new Set(
  CANONICAL_BOOKS.map((b) => b.slug),
);

let inFlight: Promise<void> | null = null;

export function ensureBibleHydrated(): Promise<void> {
  if (getReadingAccessSnapshot().restricted) {
    return countBsbBooks().then((count) => {
      if (count < CANONICAL_BOOKS.length) throw new BibleHydrationError('manifest_unavailable_no_cache');
    });
  }
  if (inFlight) return inFlight;
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

// ---- Requested-book-first fast path (2026-08-25) ----
//
// What fetchBook validates here vs the full hydration: the per-book
// path checks slug / order / chapter count / per-chapter verse
// contiguity against CANONICAL_BOOKS, but has no manifest entry so it
// cannot cross-check the per-book verse TOTAL (the trailing-verse
// witness). That stronger gate still runs on every full hydration,
// which clears + rewrites whatever this path saved. Availability now,
// full verification in the background.

const bookInFlight = new Map<string, Promise<BsbBook>>();

export function ensureBookAvailable(slug: string): Promise<BsbBook> {
  if (getReadingAccessSnapshot().restricted) {
    return getBsbBook(slug).then((book) => {
      if (!book) throw new BibleHydrationError('book_fetch_failed', { context: { slug, reason: 'not cached' } });
      return book;
    });
  }
  const existing = bookInFlight.get(slug);
  if (existing) return existing;
  const p = runEnsureBook(slug).finally(() => {
    bookInFlight.delete(slug);
  });
  bookInFlight.set(slug, p);
  return p;
}

async function runEnsureBook(slug: string): Promise<BsbBook> {
  // 1) Local IDB first. An IDB read failure falls through to the
  //    network instead of failing the read — a broken IDB must not
  //    block online reading.
  let local: BsbBook | undefined;
  try {
    local = await getBsbBook(slug);
  } catch (err) {
    console.warn('bsb_book_idb_read_failed_falling_to_network', { slug, err });
  }
  if (local) return local;

  // 2) Direct single-book fetch, validated against the canonical
  //    registry (no manifest dependency on this path).
  const entry = CANONICAL_BOOKS.find((b) => b.slug === slug);
  if (!entry) {
    throw new BibleHydrationError('book_fetch_failed', {
      context: { slug, reason: 'unknown slug' },
    });
  }
  const book = await fetchBook({
    slug: entry.slug,
    order: entry.order,
    chapters: entry.chapters,
  });

  // 3) Opportunistic persist. Fire-and-forget: the caller already has
  //    the book in memory, so a storage failure only costs the next
  //    visit a refetch.
  saveBsbBook(book).catch((err) => {
    console.warn('bsb_book_opportunistic_save_failed', { slug, err });
  });

  return book;
}

type ManifestFetchResult =
  | { ok: true; raw: unknown }
  | { ok: false; error: BibleHydrationError };

async function run(): Promise<void> {
  const storedMeta = await getBsbMeta();

  // Offline with local data: skip the manifest re-check entirely (offline-first
  // Chunk 3 §4.C). The fetch can only fail offline, and it cannot update
  // anything, so the local IDB is authoritative and skipping it makes offline
  // hydration resolve instantly with no failed-fetch latency. Gated on
  // !navigator.onLine, which is trustworthy only as "definitely offline"
  // (navigator.onLine === true is NOT trusted), so an online run still
  // re-checks the manifest for sha updates exactly as before.
  if (storedMeta && typeof navigator !== 'undefined' && !navigator.onLine) {
    return;
  }

  const fetchResult = await tryFetchManifest();

  // Have local data, no network. Local IDB is authoritative.
  if (storedMeta && !fetchResult.ok) {
    console.warn('bsb_offline_using_cache', { error: fetchResult.error });
    return;
  }

  // No local data, no network. Hard fail; the caller surfaces a retry.
  if (!storedMeta && !fetchResult.ok) {
    throw new BibleHydrationError('manifest_unavailable_no_cache', {
      cause: fetchResult.error,
    });
  }

  // fetchResult.ok must be true at this point.
  if (!fetchResult.ok) throw new Error('unreachable');
  const manifest = validateManifest(fetchResult.raw);

  // Have local data and the on-disk manifest matches the cached sha.
  // Verified completeness (2026-08-25): the meta row PROMISES the full
  // corpus, but check the cheap count witness before trusting it. A
  // partial store (interrupted legacy state, storage anomaly) routes
  // to the resumable path to repair the gaps instead of silently
  // claiming ready.
  if (storedMeta && storedMeta.sourceSha256 === manifest.sourceSha256) {
    let count: number | null = null;
    try {
      count = await countBsbBooks();
    } catch {
      // Count witness unavailable: keep the legacy trust-the-meta
      // behavior rather than triggering a spurious refetch.
      return;
    }
    if (count >= manifest.books.length) return;
    console.warn('bsb_meta_present_but_corpus_partial_repairing', { count });
    reportBibleEvent('bible_hydration_repair', {
      count,
      total: manifest.books.length,
    });
    await runResumableHydrate(manifest);
    return;
  }

  // Sha mismatch with local data: full ATOMIC re-hydrate (staged
  // update — see the header). No local data: resumable first-time
  // hydrate.
  if (storedMeta) {
    // Valid local cache exists. If rehydrate fails partway through,
    // hydrateAtomic's transaction guarantees the old IDB state is
    // intact. Keep using the old cache; the next call will retry the
    // sha-mismatch path. Same availability posture as the offline-
    // with-cache branch above.
    try {
      await runHydrate(manifest);
    } catch (err) {
      console.warn('bsb_rehydrate_failed_using_cache', {
        error: err,
        oldSha: storedMeta.sourceSha256,
        newSha: manifest.sourceSha256,
      });
      return;
    }
  } else {
    // No local data. Resumable per-book hydrate; failure must surface
    // to the caller so the index can show a retry affordance (retry
    // resumes from the checkpoint, it does not restart).
    await runResumableHydrate(manifest);
  }
}

// Resumable first-time hydration (2026-08-25). Per-book checkpointing:
// fetch → validate → write each book individually, recording progress
// in the inventory row after every durable book write, so any
// interruption (tab close, network drop, one bad request) costs only
// the unfinished books. One failed book never aborts its siblings —
// the run keeps downloading + checkpointing everything else, then
// rejects at the end so the caller's retry affordance shows; the next
// run fetches only the gaps.
async function runResumableHydrate(manifest: BsbManifest): Promise<void> {
  const store = useBibleBootstrapStore.getState();
  store.setBibleBootstrapping(true);
  try {
    const sha = manifest.sourceSha256;
    const total = manifest.books.length;

    // Seed the checkpoint. An inventory row from a DIFFERENT sha is
    // discarded (its books refetch under the new sha; same-slug puts
    // overwrite). In the meta-present repair case the store's own keys
    // are the checkpoint — meta guarantees they were written under
    // this sha. Requested-book-first warms are deliberately NOT
    // counted (their sha is unrecorded); they get refetched, trading a
    // few hundred KB for certainty.
    const saved = new Set<string>();
    try {
      const inventory = await getBsbInventory();
      if (inventory && inventory.sha === sha) {
        for (const slug of inventory.savedSlugs) saved.add(slug);
      }
      const storedMeta = await getBsbMeta();
      if (storedMeta && storedMeta.sourceSha256 === sha) {
        for (const slug of await getBsbBookSlugs()) saved.add(slug);
      }
    } catch (err) {
      console.warn('bsb_checkpoint_seed_failed_starting_fresh', { err });
      saved.clear();
    }

    const setProgress = useBibleBootstrapStore.getState().setBootstrapProgress;
    const setCounts = useBibleBootstrapStore.getState().setBootstrapCounts;
    setCounts(saved.size, total);
    setProgress(saved.size / total);

    const queue = manifest.books.filter((b) => !saved.has(b.slug));
    const failures: { slug: string; error: unknown }[] = [];

    const persistCheckpoint = async () => {
      try {
        await setBsbInventory({
          key: 'inventory',
          sha,
          savedSlugs: [...saved],
          updatedAt: Date.now(),
        });
      } catch (err) {
        // A checkpoint write failure only costs resume granularity;
        // the book itself is already durable.
        console.warn('bsb_inventory_write_failed', { err });
      }
    };

    async function worker(): Promise<void> {
      for (;;) {
        const entry = queue.shift();
        if (!entry) return;
        try {
          const book = await fetchBook(entry);
          await saveBsbBook(book);
          saved.add(entry.slug);
          setCounts(saved.size, total);
          setProgress(saved.size / total);
          await persistCheckpoint();
        } catch (err) {
          console.warn('bsb_resumable_book_failed', {
            slug: entry.slug,
            error: err,
          });
          failures.push({ slug: entry.slug, error: err });
        }
      }
    }
    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

    if (failures.length > 0) {
      throw new BibleHydrationError('book_fetch_failed', {
        cause: failures[0].error,
        context: {
          reason: 'resumable hydration incomplete',
          failedSlugs: failures.map((f) => f.slug),
          saved: saved.size,
          total,
        },
      });
    }

    // Verified-completeness gate: the meta row (the "complete Bible on
    // this device" signal) is written only after the count witness
    // confirms every book is durably present.
    const count = await countBsbBooks();
    if (count < total) {
      throw new BibleHydrationError('idb_error', {
        context: { reason: 'post-hydration count below total', count, total },
      });
    }
    await saveBsbMeta({
      key: 'manifest',
      sourceSha256: sha,
      hydratedAt: Date.now(),
      sourceEdition: manifest.sourceEdition,
    });
    await clearBsbInventory();
    clearSearchCache();
    setProgress(1);
  } finally {
    useBibleBootstrapStore.getState().setBibleBootstrapping(false);
  }
}

async function runHydrate(manifest: BsbManifest): Promise<void> {
  const store = useBibleBootstrapStore.getState();
  store.setBibleBootstrapping(true);
  store.setBootstrapProgress(0);
  try {
    const books = await fetchAllBooks(manifest);
    const meta: BsbMeta = {
      key: 'manifest',
      sourceSha256: manifest.sourceSha256,
      hydratedAt: Date.now(),
      sourceEdition: manifest.sourceEdition,
    };
    await hydrateAtomic(books, meta);
    // Sha change invalidates the in-memory search index; the next
    // searchVerses call rebuilds from the freshly hydrated books.
    clearSearchCache();
    useBibleBootstrapStore.getState().setBootstrapProgress(1);
  } finally {
    useBibleBootstrapStore.getState().setBibleBootstrapping(false);
  }
}

async function tryFetchManifest(): Promise<ManifestFetchResult> {
  try {
    const raw = await fetchManifest();
    return { ok: true, raw };
  } catch (err) {
    const error =
      err instanceof BibleHydrationError
        ? err
        : new BibleHydrationError('manifest_fetch_failed', { cause: err });
    return { ok: false, error };
  }
}

async function fetchManifest(): Promise<unknown> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(MANIFEST_URL, { signal: controller.signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw new BibleHydrationError('manifest_timeout', { cause: err });
    }
    throw new BibleHydrationError('manifest_fetch_failed', { cause: err });
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    throw new BibleHydrationError('manifest_fetch_failed', {
      context: { status: res.status },
    });
  }

  // HTML-masquerade guard: vercel.json's SPA catch-all rewrite returns
  // index.html with HTTP 200 for a MISSING /bible/bsb/* file (rule #19
  // keeps the catch-all broad on purpose), so a deploy that drops an
  // asset would otherwise surface as an opaque JSON parse error. A
  // non-JSON content-type is a missing/misrouted asset — name it.
  assertJsonResponse(res, 'manifest_fetch_failed', { url: MANIFEST_URL });

  try {
    return await res.json();
  } catch (err) {
    throw new BibleHydrationError('manifest_parse_failed', { cause: err });
  }
}

function assertJsonResponse(
  res: Response,
  code: 'manifest_fetch_failed' | 'book_fetch_failed',
  context: Record<string, unknown>,
): void {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    // The smoking-gun event for a dropped/misrouted deploy asset: a
    // Bible file answered as HTML. Reported here (in addition to the
    // downstream load-failed event) so log greps separate "asset
    // missing" from ordinary network failures.
    reportBibleEvent('bible_asset_masquerade', {
      code,
      status: res.status,
      contentType: contentType || '(none)',
      slug: typeof context.slug === 'string' ? context.slug : undefined,
    });
    throw new BibleHydrationError(code, {
      context: {
        ...context,
        status: res.status,
        contentType: contentType || '(none)',
        reason: 'non-JSON response — asset missing or SPA fallback served',
      },
    });
  }
}

function validateManifest(data: unknown): BsbManifest {
  if (!data || typeof data !== 'object') {
    throw new BibleHydrationError('manifest_invalid', {
      context: { reason: 'not an object' },
    });
  }
  const obj = data as Record<string, unknown>;

  if (obj.translation !== 'BSB') {
    throw new BibleHydrationError('manifest_invalid', {
      context: { reason: 'translation not BSB' },
    });
  }
  if (typeof obj.sourceSha256 !== 'string' || !SHA256_HEX.test(obj.sourceSha256)) {
    throw new BibleHydrationError('manifest_invalid', {
      context: { reason: 'sourceSha256 not 64-char hex' },
    });
  }
  if (typeof obj.sourceEdition !== 'string' || obj.sourceEdition.length === 0) {
    throw new BibleHydrationError('manifest_invalid', {
      context: { reason: 'sourceEdition empty' },
    });
  }
  if (!Array.isArray(obj.books) || obj.books.length !== 66) {
    throw new BibleHydrationError('manifest_invalid', {
      context: { reason: `expected 66 books, got ${Array.isArray(obj.books) ? obj.books.length : typeof obj.books}` },
    });
  }

  const seenSlugs = new Set<string>();
  const seenOrders = new Set<number>();
  for (const entry of obj.books as unknown[]) {
    if (!entry || typeof entry !== 'object') {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: 'book entry not an object' },
      });
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.slug !== 'string' || e.slug.length === 0) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: 'book slug missing' },
      });
    }
    if (typeof e.name !== 'string' || e.name.length === 0) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: `book name missing for slug ${e.slug}` },
      });
    }
    if (typeof e.order !== 'number' || !Number.isInteger(e.order)) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: `book order not integer for slug ${e.slug}` },
      });
    }
    if (typeof e.chapters !== 'number' || !Number.isInteger(e.chapters) || e.chapters <= 0) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: `book chapters not positive integer for slug ${e.slug}` },
      });
    }
    if (typeof e.verses !== 'number' || !Number.isInteger(e.verses) || e.verses <= 0) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: `book verses not positive integer for slug ${e.slug}` },
      });
    }
    seenSlugs.add(e.slug);
    seenOrders.add(e.order);
  }

  if (seenSlugs.size !== CANONICAL_SLUGS.size) {
    throw new BibleHydrationError('manifest_invalid', {
      context: { reason: `slug count ${seenSlugs.size} != canonical ${CANONICAL_SLUGS.size}` },
    });
  }
  for (const slug of seenSlugs) {
    if (!CANONICAL_SLUGS.has(slug)) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: `non-canonical slug ${slug}` },
      });
    }
  }
  for (let i = 1; i <= 66; i++) {
    if (!seenOrders.has(i)) {
      throw new BibleHydrationError('manifest_invalid', {
        context: { reason: `missing book order ${i}` },
      });
    }
  }

  return data as BsbManifest;
}

async function fetchAllBooks(manifest: BsbManifest): Promise<BsbBook[]> {
  const total = manifest.books.length;
  const setProgress = useBibleBootstrapStore.getState().setBootstrapProgress;
  const setCounts = useBibleBootstrapStore.getState().setBootstrapCounts;
  const results: (BsbBook | null)[] = new Array(total).fill(null);
  const queue = manifest.books.map((entry, idx) => ({ entry, idx }));
  let completed = 0;
  setCounts(0, total);

  async function worker(): Promise<void> {
    while (queue.length > 0) {
      const job = queue.shift();
      if (!job) return;
      const book = await fetchBook(job.entry);
      results[job.idx] = book;
      completed += 1;
      setProgress(completed / total);
      setCounts(completed, total);
    }
  }

  await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

  for (let i = 0; i < results.length; i++) {
    if (results[i] === null) {
      throw new BibleHydrationError('book_fetch_failed', {
        context: { reason: 'incomplete results', idx: i },
      });
    }
  }
  return results as BsbBook[];
}

// The expected shape for one book. The full-hydration path passes
// manifest entries (which carry the per-book verse total); the
// requested-book-first path passes canonical-registry fields with
// `verses` absent, so the total cross-check is skipped there (see the
// fast-path comment above ensureBookAvailable).
interface BookExpectation {
  slug: string;
  order: number;
  chapters: number;
  verses?: number;
}

async function fetchBook(entry: BookExpectation): Promise<BsbBook> {
  const slug = entry.slug;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), BOOK_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(bookUrl(slug), { signal: controller.signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw new BibleHydrationError('book_fetch_failed', {
        cause: err,
        context: { slug, reason: 'timeout' },
      });
    }
    throw new BibleHydrationError('book_fetch_failed', {
      cause: err,
      context: { slug, reason: 'network' },
    });
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    throw new BibleHydrationError('book_fetch_failed', {
      context: { slug, status: res.status },
    });
  }

  // Same HTML-masquerade guard as the manifest fetch: a missing book
  // file comes back as the SPA shell with HTTP 200.
  assertJsonResponse(res, 'book_fetch_failed', { slug });

  let data: unknown;
  try {
    data = await res.json();
  } catch (err) {
    throw new BibleHydrationError('book_invalid', {
      cause: err,
      context: { slug, reason: 'json_parse' },
    });
  }

  return validateBook(data, entry);
}

const VALID_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'paragraph',
  'poetry',
  'acrostic',
  'title',
  'section',
  'majorSection',
  'reference',
  'blank',
]);
const VALID_PARAGRAPH_MARKERS: ReadonlySet<string> = new Set([
  'p',
  'pmo',
  'pc',
  'li1',
  'li2',
  'd',
]);
const VALID_POETRY_INDENTS: ReadonlySet<number | string> = new Set<
  number | string
>([1, 2, 'right']);
const VALID_SECTION_LEVELS: ReadonlySet<number> = new Set([1, 2]);
const VALID_TEXTSEGMENT_KINDS: ReadonlySet<string> = new Set(['wj']);

function validateVerse(
  verse: unknown,
  slug: string,
  chapterKey: string,
  context: string,
): number {
  if (!verse || typeof verse !== 'object') {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `chapter ${chapterKey} ${context}: verse not an object` },
    });
  }
  const v = (verse as { v?: unknown }).v;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `chapter ${chapterKey} ${context}: invalid v ${String(v)}` },
    });
  }
  const vCont = (verse as { vCont?: unknown }).vCont;
  if (vCont !== undefined && vCont !== true) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} vCont must be true or omitted, got ${String(vCont)}` },
    });
  }
  const omitted = (verse as { omitted?: unknown }).omitted;
  if (omitted !== undefined && omitted !== true) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} omitted must be true or omitted, got ${String(omitted)}` },
    });
  }
  const t = (verse as { t?: unknown }).t;
  if (typeof t === 'string') {
    // ok
  } else if (Array.isArray(t)) {
    if (t.length === 0) {
      throw new BibleHydrationError('book_invalid', {
        context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} t is empty array` },
      });
    }
    for (let i = 0; i < t.length; i++) {
      const seg = t[i];
      if (!seg || typeof seg !== 'object') {
        throw new BibleHydrationError('book_invalid', {
          context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} segment ${i} not an object` },
        });
      }
      const segText = (seg as { text?: unknown }).text;
      if (typeof segText !== 'string') {
        throw new BibleHydrationError('book_invalid', {
          context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} segment ${i} text not a string` },
        });
      }
      const segKind = (seg as { kind?: unknown }).kind;
      if (segKind !== undefined && !VALID_TEXTSEGMENT_KINDS.has(segKind as string)) {
        throw new BibleHydrationError('book_invalid', {
          context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} segment ${i} invalid kind ${String(segKind)}` },
        });
      }
    }
  } else {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `chapter ${chapterKey} ${context} verse ${v} t not string or array` },
    });
  }
  return v;
}

function validateBook(data: unknown, entry: BookExpectation): BsbBook {
  const slug = entry.slug;
  if (!data || typeof data !== 'object') {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: 'not an object' },
    });
  }
  const obj = data as Record<string, unknown>;
  if (typeof obj.book !== 'string' || obj.book.length === 0) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: 'book name missing' },
    });
  }
  if (obj.slug !== slug) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `slug mismatch: payload=${String(obj.slug)}` },
    });
  }
  if (obj.order !== entry.order) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `order mismatch: payload=${String(obj.order)} expected=${entry.order}` },
    });
  }
  if (obj.translation !== 'BSB') {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: 'translation not BSB' },
    });
  }
  if (!obj.chapters || typeof obj.chapters !== 'object' || Array.isArray(obj.chapters)) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: 'chapters not an object' },
    });
  }
  const chapters = obj.chapters as Record<string, unknown>;
  const chapterKeys = Object.keys(chapters);
  if (chapterKeys.length !== entry.chapters) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `expected ${entry.chapters} chapters, got ${chapterKeys.length}` },
    });
  }
  let totalVerses = 0;
  for (let chapterNum = 1; chapterNum <= entry.chapters; chapterNum++) {
    const key = String(chapterNum);
    const blocks = chapters[key];
    if (!Array.isArray(blocks) || blocks.length === 0) {
      throw new BibleHydrationError('book_invalid', {
        context: { slug, reason: `chapter ${key} not a non-empty array of blocks` },
      });
    }

    // Set membership not positional contiguity. Document order may be
    // non-ascending (e.g. Mark 9:43-46 emits v46, v44, v43, v45,
    // v47, v48 in source order). vCont fragments deduplicate
    // naturally because the same v is added once per Set.
    const verseSet = new Set<number>();

    for (let blockIdx = 0; blockIdx < blocks.length; blockIdx++) {
      const block = blocks[blockIdx];
      if (!block || typeof block !== 'object') {
        throw new BibleHydrationError('book_invalid', {
          context: { slug, reason: `chapter ${key} block ${blockIdx} not an object` },
        });
      }
      const blockType = (block as { type?: unknown }).type;
      if (typeof blockType !== 'string' || !VALID_BLOCK_TYPES.has(blockType)) {
        throw new BibleHydrationError('book_invalid', {
          context: { slug, reason: `chapter ${key} block ${blockIdx} invalid type ${String(blockType)}` },
        });
      }

      if (blockType === 'paragraph') {
        const marker = (block as { marker?: unknown }).marker;
        if (typeof marker !== 'string' || !VALID_PARAGRAPH_MARKERS.has(marker)) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} paragraph block ${blockIdx} invalid marker ${String(marker)}` },
          });
        }
        const verses = (block as { verses?: unknown }).verses;
        if (!Array.isArray(verses) || verses.length === 0) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} paragraph block ${blockIdx} missing or empty verses array` },
          });
        }
        for (let i = 0; i < verses.length; i++) {
          const v = validateVerse(verses[i], slug, key, `paragraph block ${blockIdx} verse ${i}`);
          verseSet.add(v);
        }
      } else if (blockType === 'poetry') {
        const lines = (block as { lines?: unknown }).lines;
        if (!Array.isArray(lines) || lines.length === 0) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} poetry block ${blockIdx} missing or empty lines array` },
          });
        }
        for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
          const line = lines[lineIdx];
          if (!line || typeof line !== 'object') {
            throw new BibleHydrationError('book_invalid', {
              context: { slug, reason: `chapter ${key} poetry block ${blockIdx} line ${lineIdx} not an object` },
            });
          }
          const indent = (line as { indent?: unknown }).indent;
          if (!VALID_POETRY_INDENTS.has(indent as number | string)) {
            throw new BibleHydrationError('book_invalid', {
              context: { slug, reason: `chapter ${key} poetry block ${blockIdx} line ${lineIdx} invalid indent ${String(indent)}` },
            });
          }
          const lineVerses = (line as { verses?: unknown }).verses;
          if (!Array.isArray(lineVerses) || lineVerses.length === 0) {
            throw new BibleHydrationError('book_invalid', {
              context: { slug, reason: `chapter ${key} poetry block ${blockIdx} line ${lineIdx} missing or empty verses array` },
            });
          }
          for (let i = 0; i < lineVerses.length; i++) {
            const v = validateVerse(lineVerses[i], slug, key, `poetry block ${blockIdx} line ${lineIdx} verse ${i}`);
            verseSet.add(v);
          }
        }
      } else if (blockType === 'acrostic' || blockType === 'title' || blockType === 'majorSection') {
        const text = (block as { text?: unknown }).text;
        if (typeof text !== 'string' || text.length === 0) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} ${blockType} block ${blockIdx} missing or empty text` },
          });
        }
      } else if (blockType === 'section') {
        const text = (block as { text?: unknown }).text;
        if (typeof text !== 'string' || text.length === 0) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} section block ${blockIdx} missing or empty text` },
          });
        }
        const level = (block as { level?: unknown }).level;
        if (!VALID_SECTION_LEVELS.has(level as number)) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} section block ${blockIdx} invalid level ${String(level)}` },
          });
        }
      } else if (blockType === 'reference') {
        const text = (block as { text?: unknown }).text;
        if (typeof text !== 'string' || text.length === 0) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} reference block ${blockIdx} missing or empty text` },
          });
        }
        const locs = (block as { locs?: unknown }).locs;
        if (!Array.isArray(locs)) {
          throw new BibleHydrationError('book_invalid', {
            context: { slug, reason: `chapter ${key} reference block ${blockIdx} missing locs array` },
          });
        }
        for (let i = 0; i < locs.length; i++) {
          if (typeof locs[i] !== 'string') {
            throw new BibleHydrationError('book_invalid', {
              context: { slug, reason: `chapter ${key} reference block ${blockIdx} loc ${i} not a string` },
            });
          }
        }
      }
      // blank: type only, no other fields required
    }

    if (verseSet.size === 0) {
      throw new BibleHydrationError('book_invalid', {
        context: { slug, reason: `chapter ${key} contains no verses` },
      });
    }

    // Contiguity: verse numbers must form 1..maxV with no gaps.
    // Omitted entries are still present in the data with omitted: true,
    // so they count toward the set.
    const maxV = Math.max(...verseSet);
    for (let v = 1; v <= maxV; v++) {
      if (!verseSet.has(v)) {
        throw new BibleHydrationError('book_invalid', {
          context: { slug, reason: `chapter ${key} missing verse ${v} (have 1..${maxV} expected contiguous)` },
        });
      }
    }

    totalVerses += verseSet.size;
  }

  // Catches the trailing-verse omission case: per-chapter contiguity
  // alone can't detect a missing final verse (1..N-1 is still
  // contiguous from 1). The manifest's per-book verse total is the
  // independent witness. Skipped on the requested-book-first path,
  // which has no manifest entry (see BookExpectation).
  if (entry.verses !== undefined && totalVerses !== entry.verses) {
    throw new BibleHydrationError('book_invalid', {
      context: { slug, reason: `expected ${entry.verses} verses across ${entry.chapters} chapters, got ${totalVerses}` },
    });
  }

  return data as BsbBook;
}
